import { useState } from "react";
import axios from "axios";
import { usePageMeta } from "../hooks/usePageMeta";
import { FadeIn } from "../components/site/FadeIn";
import { SectionHeading } from "../components/site/SectionHeading";
import { CONFIG } from "../data/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROUTE_CARDS = [
  { title: "Research collaboration", copy: "For researchers and centres interested in the three questions on the Research page. Say which thread, and what a joint study would look like." },
  { title: "Doctoral or Master's enquiry", copy: "Name which of the three topics on the Lab page interests you, and what you would build first. Generic enquiries do not get replies." },
  { title: "Press and speaking", copy: "For journalists and event organisers. Include your outlet or event, the angle, and your deadline." },
  { title: "Industry or government partnership", copy: "For organisations with a problem that needs building and measuring. Describe the problem and who it affects, not the solution you have in mind." },
];

export default function Contact() {
  usePageMeta("Contact — Keyur Sorathia", "Research collaboration, doctoral enquiries, press and partnership.");
  const [form, setForm] = useState({ name: "", email: "", topic: "research", message: "" });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("sent");
      setForm({ name: "", email: "", topic: "research", message: "" });
    } catch {
      setStatus("error");
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="contact-page">
      <section className="section">
        <div className="container-site">
          <FadeIn>
            <h1 className="text-h1 mb-6" style={{ color: "var(--navy-700)" }}>Get in touch</h1>
            <p className="body-lg measure mb-12">
              Tell me which of the four below you are, and what you actually want. A specific first message gets a specific reply, and usually a faster one.
            </p>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ROUTE_CARDS.map((c) => (
              <FadeIn key={c.title}>
                <div className="h-full bg-white p-6" style={{ border: "1px solid var(--border-default)", borderRadius: 10, borderTop: "4px solid var(--accent-teal)" }} data-testid={`contact-route-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <h2 className="text-h4 mb-3" style={{ color: "var(--navy-700)" }}>{c.title}</h2>
                  <p className="body-sm m-0">{c.copy}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--surface-alt)" }}>
        <div className="container-site">
          <FadeIn>
            <SectionHeading eyebrow="Write" title="Send a message" />
          </FadeIn>
          <form onSubmit={submit} className="flex flex-col gap-6" style={{ maxWidth: 680 }} data-testid="contact-form">
            <div>
              <label className="field-label" htmlFor="contact-name">Your name</label>
              <input id="contact-name" className="field-input" required value={form.name} onChange={set("name")} data-testid="contact-name-input" />
            </div>
            <div>
              <label className="field-label" htmlFor="contact-email">Your email</label>
              <input id="contact-email" type="email" className="field-input" required value={form.email} onChange={set("email")} data-testid="contact-email-input" />
            </div>
            <div>
              <label className="field-label" htmlFor="contact-topic">I am writing about</label>
              <select id="contact-topic" className="field-input" value={form.topic} onChange={set("topic")} data-testid="contact-topic-select">
                <option value="research">Research</option>
                <option value="doctoral study">Doctoral study</option>
                <option value="press">Press</option>
                <option value="partnership">Partnership</option>
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="contact-message">Your message</label>
              <textarea id="contact-message" className="field-input" rows={6} required value={form.message} onChange={set("message")} data-testid="contact-message-input" />
            </div>
            <div>
              <button type="submit" className="btn btn-primary" disabled={status === "sending"} data-testid="contact-submit-btn">
                {status === "sending" ? "Sending" : "Send message"}
              </button>
              {status === "sent" && (
                <p className="body-sm mt-4" style={{ color: "var(--accent-teal)" }} data-testid="contact-success-message" role="status">
                  Your message has been sent.
                </p>
              )}
              {status === "error" && (
                <p className="body-sm mt-4" style={{ color: "#B42318" }} data-testid="contact-error-message" role="alert">
                  Something went wrong. Please write directly to {CONFIG.emailOfficial}.
                </p>
              )}
              <p className="caption mt-4 m-0">Nothing here is stored beyond what is needed to reply. No newsletter, no tracking.</p>
            </div>
          </form>
        </div>
      </section>

      <section className="section">
        <div className="container-site">
          <FadeIn>
            <SectionHeading eyebrow="Direct" title="Direct details" />
          </FadeIn>
          <ul className="m-0 p-0" data-testid="contact-direct-details">
            <li className="body-default py-3" style={{ borderBottom: "1px solid var(--border-default)", listStyle: "none" }}>
              <a href={`mailto:${CONFIG.emailOfficial}`} className="link-default">{CONFIG.emailOfficial}</a> (official)
            </li>
            <li className="body-default py-3" style={{ borderBottom: "1px solid var(--border-default)", listStyle: "none" }}>
              <a href={`mailto:${CONFIG.emailPersonal}`} className="link-default">{CONFIG.emailPersonal}</a> (personal)
            </li>
            <li className="body-default py-3" style={{ borderBottom: "1px solid var(--border-default)", listStyle: "none" }}>{CONFIG.phone}</li>
            <li className="body-default py-3" style={{ borderBottom: "1px solid var(--border-default)", listStyle: "none" }}>{CONFIG.address}</li>
            <li className="body-default py-3 flex flex-wrap gap-6" style={{ borderBottom: "1px solid var(--border-default)", listStyle: "none" }}>
              <a href={CONFIG.scholarUrl} target="_blank" rel="noopener noreferrer" className="link-default">Google Scholar</a>
              <a href={CONFIG.linkedinUrl} target="_blank" rel="noopener noreferrer" className="link-default">LinkedIn</a>
              <a href={CONFIG.youtubeUrl} target="_blank" rel="noopener noreferrer" className="link-default">YouTube</a>
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
}
